import React, { useState } from 'react';
import { REPORT } from '../print/PrintAtoms';
import { GradeMethodology } from './GradeCard';
import type { ParcelGrade } from '../../lib/api';

export interface DimensionDatum {
  value: string;
  label: string;
}

export interface DimensionNote {
  text: string;
  type: 'info' | 'confirmed';
}

export interface DimensionMethodology {
  inputs: { name: string; value: string; weight: string }[];
  thresholds: { grade: string; requirement: string }[];
  notScored?: string; // "Water sourcing tier is displayed but not weighted in the composite score."
  // Relabels the notScored line. Omit for the default "Not scored:" prefix. Set
  // when the text describes what the grade covers rather than what it excludes
  // (Crown Pine A1's fuel card, which is an access screen by design → "Scope:").
  notScoredLabel?: string;
}

export interface DimensionBarProps {
  grade: ParcelGrade | null;
  gradeLabel?: string;
  label: string;
  color: string;
  region?: string;
  totalParcels?: number;
  data: DimensionDatum[];
  note?: DimensionNote;
  emphasis?: boolean;
  methodology?: DimensionMethodology;
}

export const DimensionBar: React.FC<DimensionBarProps> = ({ grade, gradeLabel, label, color, region, totalParcels, data, note, emphasis, methodology }) => {
  const [expanded, setExpanded] = useState(false);
  const topPct = grade ? 100 - grade.percentile : null;
  const topLabel = topPct != null ? (topPct <= 0 ? '<1' : String(topPct)) : null;

  return (
    <div style={{
      background: REPORT.surface, borderRadius: '6px',
      padding: emphasis ? '16px 18px' : '14px 16px',
      marginBottom: '6px',
    }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: '8px' }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: '10px' }}>
          {(grade || gradeLabel) && (
            <div style={{ fontSize: '26px', fontWeight: 400, fontFamily: 'Georgia, serif', lineHeight: 1, color }}>
              {gradeLabel || grade?.grade}
            </div>
          )}
          <div style={{
            fontSize: '11px', fontWeight: 600, textTransform: 'uppercase',
            letterSpacing: '0.08em', fontFamily: 'monospace', color,
          }}>
            {label}
          </div>
        </div>
        {grade && region && (
          <div style={{ fontSize: '10px', color: REPORT.secondary }}>
            {/* Synthetic assembly grades have no assembly-aware percentile \u2014 show
                the score alone rather than a misleading cross-parcel rank. */}
            {region === 'Assembly'
              ? (grade.score != null ? `Score ${grade.score}/100` : '')
              : `Top ${topLabel}% of ${region}${grade.score != null ? ` \u00b7 Score ${grade.score}/100` : ''}`}
          </div>
        )}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
        {data.map((d, i) => (
          <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
            <div style={{
              fontSize: '9px', color: REPORT.secondary, fontFamily: 'monospace',
              letterSpacing: '0.04em', textTransform: 'uppercase',
            }}>
              {d.label}
            </div>
            <div style={{ fontSize: '11px', fontWeight: 600, color: d.value === '\u2014' ? REPORT.tertiary : REPORT.heading }}>
              {d.value}
            </div>
          </div>
        ))}
      </div>
      {note && (
        <div style={{
          fontSize: '9px', marginTop: '6px', padding: '4px 8px', borderRadius: '3px', display: 'inline-block',
          background: note.type === 'confirmed' ? 'rgba(92,107,69,0.1)' : 'rgba(154,123,48,0.1)',
          color: note.type === 'confirmed' ? REPORT.pass : REPORT.caution,
          whiteSpace: 'pre-line', lineHeight: 1.5,
        }}>
          {note.text}
        </div>
      )}
      {grade && methodology && (
        <>
          <div
            className="briefing-no-print"
            onClick={() => setExpanded(!expanded)}
            style={{ fontSize: '9px', color: REPORT.secondary, cursor: 'pointer', marginTop: '5px' }}
          >
            How we scored this {expanded ? '\u2191' : '\u2193'}
          </div>
          {expanded && (
            <div className="briefing-no-print">
              <GradeMethodology
                grade={grade} label={label} color={color}
                region={region || ''} totalParcels={totalParcels || 0}
                methodology={methodology}
              />
              {/* Two whole elements, not an inline ternary on the prefix: an
                  expression spliced into that text node changes React's SSR text
                  markers and breaks byte-identity on the protected briefings. */}
              {methodology.notScored && (methodology.notScoredLabel ? (
                <div style={{ fontSize: '9px', color: REPORT.tertiary, marginTop: '4px', fontStyle: 'italic' }}>
                  {methodology.notScoredLabel}: {methodology.notScored}
                </div>
              ) : (
                <div style={{ fontSize: '9px', color: REPORT.tertiary, marginTop: '4px', fontStyle: 'italic' }}>
                  Not scored: {methodology.notScored}
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
};
